import React, { Component } from "react";
import { addSeconds, subMinutes } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ICamera } from "@formant/core/model/ICamera";
import { IPointCloudViewConfiguration } from "@formant/core/model/IPointCloudViewConfiguration";
import { IStreamData } from "@formant/core/model/IStreamData";
import { Uuid } from "@formant/core/model/Uuid";

import { observeStore, queryStore } from "../../stores";
import { freeWebglContext, getWebglContext } from "../../utils/webglCheck";
import { DeviceSelector } from "../common/DeviceSelector";
import { DeviceTags } from "../common/DeviceTags";
import { WebglError } from "../common/WebglError";
import { Lazy } from "../Lazy";
import { IBaseModuleProps } from "./IBaseModuleProps";
import { Module } from "./Module";
import { ModuleFooter } from "./ModuleFooter";
import * as styles from "./PointCloudModule.scss";
import { currentByDevice } from "./utils/currentByDevice";
import { findConfiguration } from "./utils/findConfiguration";

const ViewerView = Lazy(async () => (await import("./ViewerView")).ViewerView);

@observer
export class PointCloudModule extends Component<IBaseModuleProps> {
    @observable
    private selectedDeviceId?: Uuid;
    private camera?: ICamera;
    private data: IStreamData<"point cloud">[] = [];
    private hasWebglContext = false;

    public componentDidMount() {
        this.hasWebglContext = getWebglContext();

        const { configuration, streamName } = this.props;
        const { camera } =
            findConfiguration(configuration, streamName, "point cloud") || {};
        this.camera = camera;
    }

    public componentWillUnmount() {
        if (this.hasWebglContext) {
            freeWebglContext();
        }
    }

    public render() {
        const { hasWebglContext, selectedDeviceId, onSelectDevice } = this;
        const { streamName, filter, focused, hovering, fullscreen } =
            this.props;
        const { time } = observeStore;

        const startTime = subMinutes(time, 1);
        const endTime = addSeconds(time, 1);

        const queryData = queryStore.moduleQuery(
            filter,
            streamName,
            "point cloud",
            startTime,
            endTime,
        );

        const loading = queryData === undefined;
        const tooMuchData = queryData === "too much data";

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const empty = data.length === 0;

        const current = currentByDevice(data, time);

        const active = current.find(
            ({ deviceId }) => selectedDeviceId === deviceId,
        );

        const deviceIds = current.map(_ => _.deviceId);

        const pointClouds = active ? [active.value] : undefined;
        return (
            <Module
                {...this.props}
                className={styles["point-cloud-module"]}
                type="point cloud"
                loading={loading}
                empty={empty}
                tooMuchData={tooMuchData}
            >
                <div className={styles.content}>
                    {hasWebglContext && (
                        <ViewerView
                            className={styles.view}
                            unfocused={!focused}
                            pointClouds={pointClouds}
                            camera={this.camera}
                            onChangeCamera={this.onChangeCamera}
                            deviceId={selectedDeviceId}
                            disableUrdf={true}
                        />
                    )}
                    <WebglError visible={!hasWebglContext} />
                </div>
                <ModuleFooter visible={hovering} floating={true}>
                    {active && selectedDeviceId && (
                        <DeviceTags
                            deviceId={selectedDeviceId}
                            tags={active.tags}
                            time={active.time}
                        />
                    )}
                    <DeviceSelector
                        selected={selectedDeviceId}
                        deviceIds={deviceIds}
                        onSelect={onSelectDevice}
                        fullscreen={fullscreen}
                    />
                </ModuleFooter>
            </Module>
        );
    }

    private onSelectDevice = (deviceId: Uuid) => {
        this.selectedDeviceId = deviceId;
    };

    private onChangeCamera = (camera: ICamera | undefined) => {
        this.changeConfiguration({ camera });
    };

    private changeConfiguration(
        updates: Partial<IPointCloudViewConfiguration>,
    ) {
        const { streamName, onChangeConfiguration } = this.props;

        if (!onChangeConfiguration) {
            return;
        }

        onChangeConfiguration({
            streamName,
            type: "point cloud",
            updates,
        });
    }
}
